import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Button from './common/Button';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ObservacaoModal from './modals/ObservacaoModal';
import CheckIcon from '@mui/icons-material/Check';
import WarningIcon from '@mui/icons-material/Warning';
import { getAuth } from 'firebase/auth';
import {
  collection,
  addDoc,
  getFirestore,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
} from 'firebase/firestore'; // Importações do Firebase Firestore

function AtendimentoForm({
  nomePaciente,
  setNomePaciente,
  clinica,
  setClinica,
  dataAtendimento,
  setDataAtendimento,
  observacao,
  setObservacao,
  clinicas,
  pacientes,
  onSubmit,
  setHistoricoAtendimentos,
}) {
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [localObservacao, setLocalObservacao] = useState(observacao);

  const db = getFirestore(); // Inicializa o Firestore

  // Carrega os dados do localStorage ao inicializar
  useEffect(() => {
    const savedNomePaciente = localStorage.getItem('nomePaciente');
    const savedClinica = localStorage.getItem('clinica');
    const savedDataAtendimento = localStorage.getItem('dataAtendimento');
    const savedObservacao = localStorage.getItem('observacao');

    if (savedNomePaciente) setNomePaciente(savedNomePaciente);
    if (savedClinica) setClinica(savedClinica);
    if (savedDataAtendimento) setDataAtendimento(savedDataAtendimento);
    if (savedObservacao) setObservacao(savedObservacao);
  }, [setNomePaciente, setClinica, setDataAtendimento, setObservacao]);

  // Salva os dados no localStorage conforme o usuário preenche
  useEffect(() => {
    localStorage.setItem('nomePaciente', nomePaciente);
  }, [nomePaciente]);

  useEffect(() => {
    localStorage.setItem('clinica', clinica);
  }, [clinica]);

  useEffect(() => {
    localStorage.setItem('dataAtendimento', dataAtendimento);
  }, [dataAtendimento]);

  // Ao salvar a observação no modal, atualizar o localStorage e o estado local
  const handleSaveObservacao = () => {
    localStorage.setItem('observacao', localObservacao);
    setObservacao(localObservacao);
    setIsModalOpen(false); // Fechar o modal
  };

  // Função para autopreencher a observação
  const handleAutoFill = () => {
    const anamnese = `
    <h3>Identificação:</h3>
    Idade: [Idade do Paciente] anos<br>
    Sexo: [Masculino/Feminino]
    <h3>Queixa Principal (QP):</h3>
    <p>Paciente relata [incluir breve descrição do sintoma principal].</p>
    <h3>História da Moléstia Atual (HMA):</h3>
    <p>Início dos sintomas há [dias/meses]. Paciente refere [detalhes do sintoma: localização, intensidade, evolução]. Tratamento inicial com [medicamentos ou ações], sem melhora significativa.</p>
    <h3>Revisão de Sistemas:</h3>
    <p>Negado febre, perda de peso, cansaço, ou outros sintomas sistêmicos relevantes.</p>
    <h3>História Patológica Pregressa:</h3>
    <p>Antecedentes de [diabetes, hipertensão, alergias, cirurgias anteriores, etc.]. Em uso de [medicações regulares].</p>
    <h3>História Familiar:</h3>
    <p>Histórico familiar de [doenças crônicas, como hipertensão, diabetes, cardiopatias, etc.].</p>
    <h3>História Social/Hábitos de Vida:</h3>
    <p>Paciente é [fumante/não fumante], ingere [álcool/sedentário/pratica atividades físicas regularmente].</p>
  `;
    setLocalObservacao(anamnese);
    toast.info('Anamnese preenchida automaticamente.', { autoClose: 3000 });
  };

  // Função para limpar a observação
  const handleClearObservacao = () => {
    setLocalObservacao('');
    toast.info('Observação limpa.', { autoClose: 3000 });
  };

  // Função para verificar e cadastrar a clínica ao submeter o formulário
  const handleClinicaCadastro = async (clinicaNome) => {
    try {
      const clinicasRef = collection(db, 'clinicas');
      const q = query(clinicasRef, where('nome', '==', clinicaNome));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        // Se a clínica não existe, adiciona ela no Firebase
        await addDoc(clinicasRef, {
          nome: clinicaNome,
          userId: getAuth().currentUser.uid, // Relaciona a clínica ao usuário logado
        });
        toast.success('Clínica cadastrada com sucesso!', {
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error('Erro ao cadastrar clínica:', error);
      toast.error('Erro ao cadastrar a clínica.');
    }
  };

  // Função para verificar e cadastrar o paciente na coleção de pacientes
  const handlePacienteCadastro = async (
    nomePaciente,
    clinica,
    dataAtendimento
  ) => {
    try {
      const pacientesRef = collection(db, 'pacientes');
      const q = query(pacientesRef, where('nome', '==', nomePaciente));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        // Se o paciente já existe, atualiza a clínica e a data de atendimento
        const pacienteDoc = querySnapshot.docs[0];
        await updateDoc(doc(db, 'pacientes', pacienteDoc.id), {
          clinica,
          dataAtendimento,
        });
        toast.success('Paciente atualizado com sucesso!', {
          autoClose: 3000,
        });
      } else {
        // Se o paciente não existe, cria um novo registro
        await addDoc(pacientesRef, {
          nome: nomePaciente,
          clinica,
          dataAtendimento,
          userId: getAuth().currentUser.uid, // Relaciona o paciente ao usuário logado
        });
        toast.success('Paciente cadastrado com sucesso!', {
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error('Erro ao cadastrar paciente:', error);
      toast.error('Erro ao cadastrar o paciente.');
    }
  };

  // Função para submissão do formulário
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) return; // Previne submissões múltiplas

    setFormError('');

    if (!nomePaciente || !clinica || !dataAtendimento) {
      setFormError('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    setIsSubmitting(true);

    try {
      const auth = getAuth();
      const medicoId = auth.currentUser ? auth.currentUser.uid : null;

      if (!medicoId) {
        throw new Error('Médico não autenticado');
      }

      // Verifica se a clínica já existe e, se não existir, cadastra uma nova
      await handleClinicaCadastro(clinica);

      // Verifica se o paciente já existe e, se não existir, cadastra um novo com nome, clínica e data de atendimento
      await handlePacienteCadastro(nomePaciente, clinica, dataAtendimento);

      // Envia os dados do atendimento para o Firebase
      await onSubmit({
        nomePaciente,
        clinica,
        dataAtendimento,
        observacao,
        medicoId, // Inclui o ID do médico no registro
      });

      // Limpa o localStorage após registrar o atendimento
      localStorage.removeItem('nomePaciente');
      localStorage.removeItem('clinica');
      localStorage.removeItem('dataAtendimento');
      localStorage.removeItem('observacao');

      // Limpa os estados do formulário
      setNomePaciente('');
      setClinica('');
      setDataAtendimento('');
      setObservacao('');
    } catch (error) {
      console.error('Erro ao registrar o atendimento:', error);
      setFormError('Erro ao registrar o atendimento. Tente novamente.');
      toast.error('Erro ao registrar o atendimento.', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExpand = (event) => {
    event.preventDefault();
    setIsModalOpen(true);
    setLocalObservacao(observacao); // Carrega a observação atual para o modal
  };

  const handleClearFields = () => {
    // Limpa os campos e remove os dados do localStorage
    setNomePaciente('');
    setClinica('');
    setDataAtendimento('');
    setObservacao('');
    localStorage.removeItem('nomePaciente');
    localStorage.removeItem('clinica');
    localStorage.removeItem('dataAtendimento');
    localStorage.removeItem('observacao');
  };

  return (
    <div>
      <ToastContainer />

      <h2 className="font-bold mb-4">Registrar Atendimento</h2>
      <div className="bg-white p-6 rounded-md shadow-md">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label htmlFor="nomePaciente" className="block font-semibold">
              Nome do Paciente:
            </label>
            <Autocomplete
              id="nomePaciente"
              freeSolo
              options={pacientes}
              getOptionLabel={(option) => option.nome || ''}
              value={pacientes.find((p) => p.nome === nomePaciente) || null}
              onChange={(event, newValue) => {
                if (newValue && typeof newValue === 'object') {
                  setNomePaciente(newValue.nome);
                } else {
                  setNomePaciente(newValue);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  id="nomePaciente"
                  name="nomePaciente"
                  placeholder="Nome do Paciente"
                  variant="outlined"
                  onChange={(e) => setNomePaciente(e.target.value)}
                  required
                />
              )}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="clinica" className="block font-semibold">
              Clínica:
            </label>
            <Autocomplete
              id="clinica"
              freeSolo
              options={clinicas}
              getOptionLabel={(option) => option.nome || ''}
              value={clinicas.find((c) => c.nome === clinica) || null}
              onChange={(event, newValue) => {
                if (newValue && typeof newValue === 'object') {
                  setClinica(newValue.nome);
                } else {
                  setClinica(newValue);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  id="clinica"
                  name="clinica"
                  placeholder="Clínica de Atendimento"
                  variant="outlined"
                  onChange={(e) => setClinica(e.target.value)}
                  required
                />
              )}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="dataAtendimento" className="block font-semibold">
              Data e Hora do Atendimento:
            </label>
            <input
              type="datetime-local"
              id="dataAtendimento"
              name="dataAtendimento"
              value={dataAtendimento}
              onChange={(e) => setDataAtendimento(e.target.value)}
              className="w-full border rounded-md p-2"
              required
            />
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-between items-center mt-6 space-y-4 sm:space-y-0 gap-5">
            <div className="flex space-x-4 w-full sm:w-auto">
              <Button
                type="expandir"
                label={
                  observacao ? (
                    <div className="flex items-center">
                      Anamnese <CheckIcon className="text-green-500 ml-2" />
                    </div>
                  ) : (
                    <div className="flex items-center">
                      Anamnese <WarningIcon className="text-red-500 ml-2" />
                    </div>
                  )
                }
                onClick={handleExpand}
                className="w-full sm:w-auto"
              />

              <Button
                type="limpar"
                label="Limpar"
                className="w-full sm:w-auto"
                onClick={handleClearFields}
              />
            </div>

            <Button
              type="salvar"
              label={isSubmitting ? 'Registrando...' : 'Registrar Atendimento'}
              className="w-full sm:w-auto ml-0 sm:ml-auto mt-4 sm:mt-0"
              disabled={isSubmitting}
            />
          </div>
        </form>

        {formError && <div className="text-red-500 mt-2">{formError}</div>}

        <ObservacaoModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          value={localObservacao}
          onChange={setLocalObservacao}
          onSave={handleSaveObservacao}
          handleAutoFill={handleAutoFill}
          handleClearObservacao={handleClearObservacao}
        />
      </div>
    </div>
  );
}

AtendimentoForm.propTypes = {
  nomePaciente: PropTypes.string.isRequired,
  setNomePaciente: PropTypes.func.isRequired,
  clinica: PropTypes.string.isRequired,
  setClinica: PropTypes.func.isRequired,
  dataAtendimento: PropTypes.string.isRequired,
  setDataAtendimento: PropTypes.func.isRequired,
  observacao: PropTypes.string.isRequired,
  setObservacao: PropTypes.func.isRequired,
  clinicas: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      nome: PropTypes.string.isRequired,
      userId: PropTypes.string.isRequired,
    })
  ).isRequired,
  pacientes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      nome: PropTypes.string.isRequired,
    })
  ).isRequired,
  onSubmit: PropTypes.func.isRequired,
  setHistoricoAtendimentos: PropTypes.func.isRequired,
};

export default AtendimentoForm;
